/**
 * There is an issue with react-hooks saying that useMemo is being used conditionally when it is not.
 */
import React, { useMemo } from "react";
import cn from "clsx";
import { TransformMap, ValidationRules } from "objectro/lib/types";
import { transform, validate } from "objectro";
import merge from "lodash/merge";
import {
  I18nMessage,
  ConfigurableFormTFn,
  ConfigurableFormShowOnlyIfFn,
  ConfigurableFormStateToPropsFn,
  ConfigurableFormErrorsObject,
  ConfigurableFormElementErrors,
  ConfigurableFormElements
} from "../../types";
import { has, get, toArray, isObject, isFunction } from "../../utils/helpers";
import { translateLabels, translateOptionsLabels, DEFAULT_ELEMENTS } from ".";

export interface SharedFormElementProps {
  /**
   * The shared form state between all form elements.
   */
  sharedState: object;
  /**
   * The form's errors object.
   */
  errors: ConfigurableFormErrorsObject;
  /**
   * The object of elements' render components.
   */
  elements?: ConfigurableFormElements;
  /**
   * The shared i18next translation function.
   */
  t: ConfigurableFormTFn;
}

export interface FormElementProps {
  /**
   * The key for the form element (<FormElements /> renders each form element
   * and React requires that rendering elements in an `Array.map` function
   * require a key).
   */
  key: string;
  /**
   * The component used to render the form element.
   */
  component: (props: any) => JSX.Element | null;
  /**
   * The specific props for this element.
   */
  props?: any;
  /**
   * A transform map or function to take the `sharedState` and map to
   * the rendered element's props.
   */
  stateToProps?: TransformMap | ConfigurableFormStateToPropsFn;
  /**
   * An `objectro.validate` ValidationRules object, or a function that
   * takes the `sharedState` object and returns a boolean.
   */
  showOnlyIf?: boolean | ValidationRules | ConfigurableFormShowOnlyIfFn;
  /**
   * The props related to the form which are shared between all form elements.
   */
  formProps: SharedFormElementProps;
}

export interface RenderElementProps {
  id?: string;
  type?: string;
  name?: string;
  value?: string;
  label?: string | I18nMessage;
  errors?: ConfigurableFormElementErrors;
  className?: string;
  display?: boolean;
  formProps: SharedFormElementProps;
}

export default function FormElement({
  component,
  props = {},
  stateToProps = {},
  showOnlyIf = true,
  formProps: { sharedState, errors, elements = DEFAULT_ELEMENTS, t }
}: FormElementProps) {
  // Element props
  const {
    id = "",
    name = "",
    type = "",
    value = "",
    label = "",
    ...rest
  } = props;

  // Preferrably use the `pageState.language`, otherwise default
  // to the `appState.language`
  const lang = has(sharedState, "pageState.language")
    ? get(sharedState, "pageState.language")
    : get(sharedState, "appState.language");

  const _display: boolean = useMemo(() => {
    // Use the ValidationRules object
    if (isObject(showOnlyIf)) {
      return validate(sharedState, showOnlyIf);
    }

    // Use the custom function
    if (isFunction(showOnlyIf)) {
      return (showOnlyIf as Function)(sharedState);
    }

    // use the boolean type
    return !!showOnlyIf;
  }, [sharedState, showOnlyIf]);

  const _props: RenderElementProps = useMemo(() => {
    // Map any state to props
    const _stateToProps = transform(sharedState, stateToProps);
    const _combinedProps = merge({}, { ...rest, ...props }, _stateToProps);

    // Translate any provided labels
    if (_combinedProps.labels) {
      _combinedProps.labels = translateLabels(_combinedProps.labels, t);
    }

    // Translate any subfield labels
    if (has(_combinedProps, "inputProps.labels")) {
      _combinedProps.inputProps.labels = translateLabels(
        _combinedProps.inputProps.labels,
        t
      );
    }

    // Translate any options' labels
    if (has(_combinedProps, "options")) {
      _combinedProps.options = translateOptionsLabels(
        get(_combinedProps, "options"),
        t
      );
    }
    if (has(_combinedProps, "inputProps.options")) {
      _combinedProps.inputProps.options = translateOptionsLabels(
        get(_combinedProps, "inputProps.options"),
        t
      );
    }

    // Add special class to hide element, since using a `null` to render
    // the element seems to break the app.
    _combinedProps.className = cn(
      _combinedProps.className,
      "configurable-form-field",
      !_display && "hidden"
    );

    // Special consideration for form elements that require more info:
    // needs to feed through extra formProps so the rendered element
    // can do more advanced behaviours.
    if (has(component, "renderWithFormProps")) {
      _combinedProps.formProps = {
        sharedState,
        errors,
        elements,
        t
      };
    }

    return _combinedProps;
  }, [t, props, stateToProps, rest, _display]);

  // Extract the element's errors object based on the element's name
  const _errors: object = useMemo(() => {
    if (has(errors, name)) {
      return toArray(get(errors, name));
    }
    return [];
  }, [errors, name]);

  // Pluck the value from the form state based on the element's name
  const _value: string = useMemo(() => {
    if (has(sharedState, ["formState", name])) {
      return get(sharedState, ["formState", name]);
    }
    return value;
  }, [sharedState, name]);

  // Translate the label
  const _label: React.ReactNode = useMemo(() => t(label), [t]);

  // Render the element based on the component given in the `ConfigurableForms.elements` object
  const RenderComponent = component;
  return (
    <RenderComponent
      {..._props}
      id={id}
      name={name}
      type={type}
      label={_label}
      value={_value}
      errors={_errors}
      display={_display}
      lang={lang}
    />
  );
}
